import { useStore, TocItem } from '../../store/useStore';
import { FiX, FiChevronRight, FiChevronDown, FiBookOpen } from 'react-icons/fi';
import { useState } from 'react';

// 전역 함수 타입
declare global {
  interface Window {
    epubGoTo?: (href: string) => void;
    pdfGoToPage?: (page: number) => void;
  }
}

const TOCPanel = () => {
  const { toggleTOC, tocItems, tocType, currentBook } = useStore();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleItemClick = (item: TocItem) => {
    console.log('📖 목차 클릭:', item.label);
    console.log('   타입:', tocType);
    console.log('   href:', item.href);
    console.log('   페이지:', item.pageNumber);
    
    if (tocType === 'epub') {
      // EPUB: href로 이동
      if (window.epubGoTo && item.href) {
        console.log('   ➡️ EPUB 이동 실행');
        window.epubGoTo(item.href);
        toggleTOC();
      } else {
        console.warn('   ❌ epubGoTo 없음 또는 href 없음');
      }
    } else if (tocType === 'pdf') {
      // PDF: 페이지 번호로 이동
      if (window.pdfGoToPage && item.pageNumber) {
        console.log('   ➡️ PDF 페이지 이동 실행:', item.pageNumber);
        window.pdfGoToPage(item.pageNumber);
        toggleTOC();
      } else {
        console.warn('   ❌ pdfGoToPage 없음:', window.pdfGoToPage, '페이지:', item.pageNumber);
      }
    }
  };

  const renderTOCItem = (item: TocItem, level: number = 0) => {
    const hasSubitems = item.subitems && item.subitems.length > 0;
    const isExpanded = expandedItems.has(item.id);

    return (
      <div key={item.id}>
        <div
          className={`flex items-center gap-2 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer border-b border-gray-100 dark:border-gray-700 ${
            level > 0 ? 'bg-gray-50 dark:bg-gray-800' : ''
          }`}
          style={{ paddingLeft: `${16 + level * 16}px` }}
        >
          {/* 확장/축소 버튼 */}
          {hasSubitems ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(item.id);
              }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
            >
              {isExpanded ? (
                <FiChevronDown size={16} className="text-gray-500" />
              ) : (
                <FiChevronRight size={16} className="text-gray-500" />
              )}
            </button>
          ) : (
            <span className="w-6" />
          )}

          {/* 목차 항목 */}
          <button
            onClick={() => handleItemClick(item)}
            className="flex-1 text-left text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <span className="line-clamp-2">{item.label}</span>
          </button>

          {/* PDF: 페이지 번호 표시 */}
          {tocType === 'pdf' && item.pageNumber && (
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 whitespace-nowrap">
              {item.pageNumber}p
            </span>
          )}
        </div>

        {/* 하위 항목 */}
        {hasSubitems && isExpanded && (
          <div className="bg-gray-50 dark:bg-gray-850">
            {item.subitems!.map((subitem) => renderTOCItem(subitem, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 w-80 h-full shadow-xl overflow-hidden flex flex-col">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <FiBookOpen size={20} className="text-blue-500" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            내용 목록
          </h2>
        </div>
        <button
          onClick={toggleTOC}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <FiX size={20} />
        </button>
      </div>

      {/* 책 정보 */}
      {currentBook && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
            {currentBook.title}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {currentBook.author}
            </span>
            <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded">
              {tocType?.toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* TOC Content */}
      <div className="flex-1 overflow-y-auto">
        {tocItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <FiBookOpen size={48} className="mb-4 opacity-30" />
            <p>목차를 불러오는 중...</p>
            <p className="text-xs mt-2">또는 목차가 없는 파일입니다</p>
          </div>
        ) : (
          <div>
            {/* 목차 통계 */}
            <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900">
              총 {tocItems.length}개 항목
            </div>
            
            {/* 목차 목록 */}
            {tocItems.map((item) => renderTOCItem(item))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TOCPanel;
